/**
 * Bloqueo de edición por registro.
 *
 * El primero que abre un registro para editarlo se lo queda; el resto lo ve en solo lectura y el
 * servidor rechaza sus escrituras. El bloqueo caduca por **inactividad**, no por tener la pestaña
 * abierta: una pestaña olvidada no puede parar el trabajo de los demás para siempre.
 */

/** Tipos de registro que se pueden bloquear. La API valida contra esta lista. */
export const EDIT_LOCK_RESOURCES = [
  'sales_order',
  'customer',
  'product',
  'family',
  'supplier',
  'mirror_model',
  'bom_version',
  'purchase_invoice',
  'user',
  'terminal',
  'payment_method'
] as const;

export type EditLockResource = (typeof EDIT_LOCK_RESOURCES)[number];

/** Nombres para la pantalla: «Lo está editando Ana — Pedidos de venta». */
export const EDIT_LOCK_LABELS: Record<EditLockResource, string> = {
  sales_order: 'el pedido de venta',
  customer: 'la ficha del cliente',
  product: 'el artículo',
  family: 'la familia',
  supplier: 'el proveedor',
  mirror_model: 'el modelo',
  bom_version: 'la versión de la BOM',
  purchase_invoice: 'la factura de compra',
  user: 'el usuario',
  terminal: 'el terminal',
  payment_method: 'la forma de pago'
};

/** Quince minutos desde la última actividad. */
export const EDIT_LOCK_TTL_MS = 15 * 60 * 1000;
/** Latido: solo se manda si hubo actividad desde el anterior. */
export const EDIT_LOCK_HEARTBEAT_MS = 60 * 1000;
/** A partir de aquí se avisa de que el bloqueo está a punto de caducar. */
export const EDIT_LOCK_WARNING_MS = 2 * 60 * 1000;

export function isEditLockResource(value: string): value is EditLockResource {
  return (EDIT_LOCK_RESOURCES as readonly string[]).includes(value);
}

/** Identificador compuesto que se publica por SSE y se usa como clave del advisory lock. */
export function editLockKey(resourceType: string, resourceId: string): string {
  return `${resourceType}:${resourceId}`;
}
